import React, { useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "../../../theme";
import {
  SlidePanel,
  PanelHeader,
  PanelCloseButton,
  panelUpVariants,
  panelDownVariants,
} from "../../atoms/SlideActionPanel";
import Subtitle from "../../atoms/typography/Subtitle";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Divider from "../../atoms/Divider";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import Checkbox from "../../atoms/inputs/Checkbox";
import { InfoIconSmall } from "../../atoms/icons/InfoIcons";
import Tooltip from "../Tooltip";
import BodySmall from "../../atoms/typography/BodySmall";
import Alert from "../Alert";
import TitleSmall from "../../atoms/typography/TitleSmall";

export const NumberOfSelectedContainer = styled.div`
  padding-right: 16px;
  border-right: 1.5px solid ${theme.colors.grey10};
  margin-right: auto;
`;

const OptionRow = styled.div`
  padding: 6px 4px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

interface Tag {
  key: string;
  labelKey: string;
  selected: boolean | "indeterminate";
}

interface SlidePanelProps {
  panelRef: RefObject<HTMLDivElement | null>;
  panelDirection: "up" | "down";
  closePanel: () => void;
  selectedCount: number;
}

interface TagSlidePanelProps extends SlidePanelProps {
  tags?: Tag[];
  onApply: (add: string[], remove: string[]) => Promise<void>;
}

export const TagSlidePanel: React.FC<TagSlidePanelProps> = ({
  panelRef,
  panelDirection,
  closePanel,
  selectedCount,
  tags = [],
  onApply,
}) => {
  const { t } = useTranslation();
  const [localTags, setLocalTags] = useState(tags);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const onToggle = (key: string) =>
    setLocalTags((prev) =>
      prev.map((tag) =>
        tag.key === key
          ? { ...tag, selected: tag.selected === true ? false : true }
          : tag,
      ),
    );

  const handleApply = async () => {
    const originalMap = new Map(tags.map((t) => [t.key, t.selected]));
    const add = localTags
      .filter((t) => t.selected === true && originalMap.get(t.key) !== true)
      .map((t) => t.key);
    const remove = localTags
      .filter((t) => t.selected === false && originalMap.get(t.key) !== false)
      .map((t) => t.key);
    setError(undefined);
    setLoading(true);
    try {
      await onApply(add, remove);
      closePanel();
    } catch {
      setError(t("invoices.updateTagsError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlidePanel
      ref={panelRef}
      direction={panelDirection}
      variants={panelDirection === "up" ? panelUpVariants : panelDownVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <PanelHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <TitleSmall color={theme.colors.primary}>
            {t("invoices.tag", { count: selectedCount })}
          </TitleSmall>
          <Tooltip
            content={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ filter: "invert(1)" }}>
                    <Checkbox small checked={true} onChange={() => {}} />
                  </div>
                  <BodySmall color={theme.colors.white}>
                    {t("invoices.tagInfoAllSelected")}
                  </BodySmall>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ filter: "invert(1)" }}>
                    <Checkbox
                      small
                      checked={false}
                      indeterminate={true}
                      onChange={() => {}}
                    />
                  </div>
                  <BodySmall color={theme.colors.white}>
                    {t("invoices.tagInfoSomeSelected")}
                  </BodySmall>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ filter: "invert(1)" }}>
                    <Checkbox small checked={false} onChange={() => {}} />
                  </div>
                  <BodySmall color={theme.colors.white}>
                    {t("invoices.tagInfoNoneSelected")}
                  </BodySmall>
                </div>
              </div>
            }
            direction="right"
          >
            <InfoIconSmall color={theme.colors.primary} />
          </Tooltip>
        </div>
        <PanelCloseButton onClick={closePanel}>
          <CrossIcon color={theme.colors.text} />
        </PanelCloseButton>
      </PanelHeader>
      {localTags.map((tag) => (
        <OptionRow key={tag.key} onClick={() => onToggle(tag.key)}>
          <Checkbox
            checked={tag.selected === true}
            indeterminate={tag.selected === "indeterminate"}
            onChange={() => onToggle(tag.key)}
            title={t(tag.labelKey)}
          />
        </OptionRow>
      ))}
      <Divider />
      <HorizontalSpacer spacing={1} />
      {error && <Alert type="error" title={error} />}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 24 }}>
        <DefaultButton
          title={t("common.cancel")}
          color={theme.colors.primary}
          onClick={closePanel}
        />
        <ActionButton
          title={t("common.save")}
          onClick={handleApply}
          color={theme.colors.primary}
          disabled={loading}
        />
      </div>
    </SlidePanel>
  );
};

export const RejectSlidePanel: React.FC<SlidePanelProps> = ({
  panelRef,
  panelDirection,
  closePanel,
  selectedCount,
}) => {
  const { t } = useTranslation();
  return (
    <SlidePanel
      ref={panelRef}
      direction={panelDirection}
      variants={panelDirection === "up" ? panelUpVariants : panelDownVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <PanelHeader>
        <Subtitle color={theme.colors.primary}>
          {t("invoiceAssessment.rejectInvoices", { count: selectedCount })}
        </Subtitle>
        <PanelCloseButton onClick={closePanel}>
          <CrossIcon color={theme.colors.text} />
        </PanelCloseButton>
      </PanelHeader>
      <Divider />
      <HorizontalSpacer spacing={1} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 24 }}>
        <DefaultButton title={t("common.cancel")} onClick={closePanel} />
        <ActionButton
          title={t("documentHandler.reject")}
          onClick={() => {}}
          color={theme.colors.primary}
        />
      </div>
    </SlidePanel>
  );
};
